import { Button } from '@cambridgeassessment/cambridge-ui';
import { Box } from '@material-ui/core';
import flatten from 'flat';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import useNotifications from 'src/hooks/useNotifications';
import { setSubscriptions } from 'src/services/gigya/gigya-service';
import useAuth from '../../hooks/useAuth';
import {
  SubscriptionsSchema,
  SubscriptionsSchemaFieldWithName,
} from '../../types';
import AutocompleteWithTags from '../AutocompleteWithTags/AutocompleteWithTags';
import PreferenceCard from '../PreferenceCard/PreferenceCard';
import {
  constructSubscriptionsObject,
  extractThemaSubjectsFromSchema,
  hasSelectionChanged,
} from './utils';

type Props = {
  subscriptionsSchema: SubscriptionsSchema;
};

const MyInterestsPreference: React.FC<Props> = ({ subscriptionsSchema }) => {
  const { user, update } = useAuth();
  const { addNotification } = useNotifications();

  const themaSubjects = useMemo(() => {
    return extractThemaSubjectsFromSchema(
      subscriptionsSchema?.fields || {}
    ).sort((a, b) => a.email.description.localeCompare(b?.email?.description));
  }, [subscriptionsSchema?.fields]);

  const flatUserThemaSubscriptions = useMemo(() => {
    return flatten(user?.subscriptions || {}, {
      maxDepth: 2,
    });
  }, [user?.subscriptions]);

  const selectedThemaSubjectsValue = useMemo(() => {
    return themaSubjects.filter(
      (subject) => flatUserThemaSubscriptions[subject.name]?.email?.isSubscribed
    );
  }, [themaSubjects, flatUserThemaSubscriptions]);

  const [selectedThemaSubjects, setSelectedThemaSubjects] = useState(
    selectedThemaSubjectsValue.length ? selectedThemaSubjectsValue : null
  );
  const [savedThemaSubjects, setSavedThemaSubjects] = useState(
    selectedThemaSubjects
  );
  const handleSave = useCallback(() => {
    setSubscriptions(
      constructSubscriptionsObject(themaSubjects, selectedThemaSubjects),
      update
    );
    setSavedThemaSubjects(selectedThemaSubjects);
    addNotification({ title: 'Subscriptions saved!' });
  }, [addNotification, selectedThemaSubjects, themaSubjects, update]);

  const handleCancel = useCallback(() => {
    setSelectedThemaSubjects(
      savedThemaSubjects?.length ? [...savedThemaSubjects] : null
    );
  }, [savedThemaSubjects]);

  const handleAutoselectChange = useCallback(
    (_event: React.ChangeEvent, value: SubscriptionsSchemaFieldWithName) => {
      if (value) {
        setSelectedThemaSubjects(
          Array.isArray(selectedThemaSubjects)
            ? [value, ...selectedThemaSubjects].sort((a, b) =>
                a.email.description.localeCompare(b?.email?.description)
              )
            : [value]
        );
      }
    },
    [selectedThemaSubjects]
  );

  const handleInterestDelete = useCallback(
    (index) => {
      const val = selectedThemaSubjects.filter(
        (_subject, subjectIndex) => subjectIndex !== index
      );
      setSelectedThemaSubjects(val.length ? val : null);
    },
    [selectedThemaSubjects]
  );

  const getItemLabel = useCallback((option) => {
    return option?.email?.description || '';
  }, []);

  useEffect(() => {
    setSelectedThemaSubjects(
      selectedThemaSubjectsValue.length ? selectedThemaSubjectsValue : null
    );
  }, [selectedThemaSubjectsValue]);

  return (
    <PreferenceCard
      title="My Interests"
      subtitle="Subscribe to general updates from Cambridge about subjects, syllabuses and more."
    >
      <AutocompleteWithTags
        allSubjects={themaSubjects}
        selectedSubjects={selectedThemaSubjects}
        header="Subjects"
        subheader="Add a subject, or subject area to your interests to subscribe to general information about that subject or subject area."
        getLabel={getItemLabel}
        onChange={handleAutoselectChange}
        onDelete={handleInterestDelete}
      ></AutocompleteWithTags>
      <Box mt={3} display="flex">
        <Box mr={1}>
          <Button
            data-testid="cancelButton"
            disabled={
              !hasSelectionChanged(selectedThemaSubjects, savedThemaSubjects)
            }
            onClick={handleCancel}
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
        <Box>
          <Button
            data-testid="saveButton"
            disabled={
              !hasSelectionChanged(selectedThemaSubjects, savedThemaSubjects)
            }
            onClick={handleSave}
          >
            Save changes
          </Button>
        </Box>
      </Box>
    </PreferenceCard>
  );
};

export default hot(memo(MyInterestsPreference));
